import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

import { Menu, MenuItem } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMaterialUIController, setLayout } from "context";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS, FONT } from "theme/tokens";

function SignInLayout({ children }) {
  const { language, changeLanguage, componentLanguageData, loadLanguageFile } =
    useContext(LanguageContext);

  // 2026-05-25 語系下拉（跟登入後 DashboardNavbar 同款 Box + Menu pattern）
  const [anchorElLang, setAnchorElLang] = useState(null);

  // 模擬 PageLayout 的副作用：把全域 layout 標記成 "page"，這樣 Sidenav 不會顯示
  const [, dispatch] = useMaterialUIController();
  const { pathname } = useLocation();
  useEffect(() => {
    setLayout(dispatch, "page");
  }, [pathname, dispatch]);

  useEffect(() => {
    loadLanguageFile("footer", "footer/footer_lang");
  }, [language, loadLanguageFile]);

  const FOOTER_LANG = componentLanguageData.footer || { Main: {} };

  return (
    <MDBox
      width="100%"
      display="flex"
      flexDirection="column"
      flex={1}
      sx={{ backgroundColor: COLORS.grayLighter, overflowX: "hidden" }}
    >
      {/* === Top header === */}
      <MDBox
        component="header"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={{ xs: 2, md: 4 }}
        py={1.5}
        sx={{
          backgroundColor: COLORS.white,
          borderBottom: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <MDBox
          component="a"
          href="https://masquant.com/"
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer", textDecoration: "none" }}
        >
          <img
            src="/images/logo/logo.png"
            alt="MAS"
            style={{ height: 36, objectFit: "contain" }}
          />
        </MDBox>

        <MDBox display="flex" alignItems="center" gap={1.5}>
          <MDButton
            component="a"
            href="https://masquant.com/#contact"
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            color="info"
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontFamily: FONT.NOTO,
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: 1,
              minHeight: 30,
              height: 30,
              px: 2, // 16px 左右
              py: 0,
              color: COLORS.main, // #F36D26
              borderColor: COLORS.main,
              "&:hover": {
                borderColor: COLORS.main,
                backgroundColor: COLORS.lighter,
                color: COLORS.main,
              },
            }}
          >
            {FOOTER_LANG.Main.Content5 || "聯絡我們"}
          </MDButton>

          {/* 2026-05-25 語系下拉：Box + Menu + chevron（跟登入後 DashboardNavbar 一致）*/}
          {(() => {
            const langLabelMap = {
              "zh-TW": "繁中",
              "zh-CN": "简中",
              "en-US": "EN (US)",
              "en-GB": "EN (UK)",
            };
            const currentLangLabel = langLabelMap[language] || "繁中";
            return (
              <>
                <MDBox
                  onClick={(e) => setAnchorElLang(e.currentTarget)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                    px: 1, // 8px
                    height: 30,
                    minWidth: 74,
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    cursor: "pointer",
                    fontFamily: FONT.NOTO,
                    fontSize: "12px",
                    fontWeight: 400,
                    color: COLORS.black, // #000
                    background: COLORS.white,
                    transition: "all 0.15s ease",
                    "&:hover": { borderColor: COLORS.grayMedian, background: COLORS.grayLightest },
                  }}
                >
                  <span style={{ lineHeight: 1 }}>{currentLangLabel}</span>
                  <KeyboardArrowDownIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
                </MDBox>
                <Menu
                  anchorEl={anchorElLang}
                  open={Boolean(anchorElLang)}
                  onClose={() => setAnchorElLang(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 140,
                      borderRadius: "10px",
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)",
                      "& .MuiMenuItem-root": {
                        fontSize: "14px",
                        py: 1,
                        px: 1.5,
                        "&.Mui-selected": {
                          background: COLORS.lighter,
                          color: COLORS.main,
                          fontWeight: 600,
                        },
                        "&:hover": { background: COLORS.grayLightest },
                      },
                    },
                  }}
                >
                  {[
                    { code: "zh-TW", label: "繁體中文" },
                    { code: "zh-CN", label: "简体中文" },
                    { code: "en-US", label: "English (US)" },
                    { code: "en-GB", label: "English (UK)" },
                  ].map((opt) => (
                    <MenuItem
                      key={opt.code}
                      selected={language === opt.code}
                      onClick={() => {
                        changeLanguage(opt.code);
                        setAnchorElLang(null);
                      }}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            );
          })()}
        </MDBox>
      </MDBox>

      {/* === Main === */}
      <MDBox
        component="main"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
        px={2}
        py={{ xs: 4, md: 6 }}
      >
        {children}
      </MDBox>
    </MDBox>
  );
}

SignInLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SignInLayout;
